import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import {
  addDaysToDateOnly,
  dateOnlyFromInstant,
  isValidTimeZone,
  isRealInstantWithinScheduledWallRange,
  parseDateOnly,
  parseScheduledWall,
  realInstantAsSalonWallEpoch,
  scheduledWallRangeToRealInstants,
  weekdayForDateOnly,
} from "@/lib/salon-time";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export type CurrencyDisplay = {
  symbol: string;
  code: string;
  factor: number;
};

type CurrencyInput =
  | string
  | Partial<CurrencyDisplay>
  | Array<Partial<CurrencyDisplay>>
  | null
  | undefined;

const DEFAULT_CURRENCY: CurrencyDisplay = {
  symbol: "₦",
  code: "NGN",
  factor: 100,
};

/** Payment widgets reject NGN charges below this amount. */
export const MIN_CHECKOUT_AMOUNT_NGN = 100;

export function isBelowMinNgnCheckoutAmount(
  amount: number,
  currency: CurrencyInput = DEFAULT_CURRENCY,
): boolean {
  const currencyDetails = normalizeCurrency(currency);
  if (currencyDetails.code !== "NGN") return false;
  return Number(amount) < MIN_CHECKOUT_AMOUNT_NGN;
}

export function getSelectedProductsGoodsTotal(
  products: Array<{
    retailPrice?: number | null;
    quantity?: number | null;
  }> = [],
): number {
  return products.reduce(
    (sum, product) => sum + (product.retailPrice || 0) * (product.quantity || 1),
    0,
  );
}

function normalizeCurrency(currency: CurrencyInput): CurrencyDisplay {
  if (typeof currency === "string") {
    return { ...DEFAULT_CURRENCY, symbol: currency };
  }

  const value = Array.isArray(currency) ? currency[0] : currency;
  const factor = Number(value?.factor || DEFAULT_CURRENCY.factor);

  return {
    symbol: value?.symbol || DEFAULT_CURRENCY.symbol,
    code: value?.code || DEFAULT_CURRENCY.code,
    factor: Number.isFinite(factor) && factor > 0 ? factor : DEFAULT_CURRENCY.factor,
  };
}

export function getBusinessCurrency(businessInfo?: {
  country?: { currency?: CurrencyInput } | null;
} | null): CurrencyDisplay {
  return normalizeCurrency(businessInfo?.country?.currency);
}

function fractionDigitsForFactor(factor: number): number {
  if (!Number.isFinite(factor) || factor <= 1) return 0;

  return Math.max(0, Math.round(Math.log10(factor)));
}

export function formatPrice(
  price: number | undefined | null,
  currency: CurrencyInput = DEFAULT_CURRENCY
): string {
  const currencyDetails = normalizeCurrency(currency);

  if (price === undefined || price === null || isNaN(price)) {
    return `${currencyDetails.symbol}0`;
  }

  const fractionDigits = fractionDigitsForFactor(currencyDetails.factor);
  const hasFraction = price % 1 !== 0;

  return `${currencyDetails.symbol}${price.toLocaleString(undefined, {
    minimumFractionDigits: hasFraction ? fractionDigits : 0,
    maximumFractionDigits: fractionDigits,
  })}`;
}

export function getMinNgnCheckoutToastMessage(
  currency: CurrencyInput = DEFAULT_CURRENCY,
): string {
  return `Minimum checkout amount is ${formatPrice(MIN_CHECKOUT_AMOUNT_NGN, currency)}. Add more items to continue.`;
}

export function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes}min`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) {
    return `${hours}hr${hours > 1 ? "s" : ""}`;
  }
  return `${hours}hr ${mins}min`;
}

export function toSentenceCase(categoryName: string): string {
  return categoryName.replace(/_/g, " ");
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/--+/g, "-")
    .trim();
}

export function formatTime(timeString?: string): string {
  if (!timeString) return "";

  const [hours, minutes] = timeString.split(":");

  // Check if hours or minutes are invalid
  if (
    !hours ||
    !minutes ||
    isNaN(parseInt(hours, 10)) ||
    isNaN(parseInt(minutes, 10))
  ) {
    return "";
  }

  let period = "AM";
  let formattedHours = parseInt(hours, 10);

  if (formattedHours > 12) {
    formattedHours -= 12;
    period = "PM";
  } else if (formattedHours === 12) {
    period = "PM";
  } else if (formattedHours === 0) {
    formattedHours = 12;
  }

  // Ensure minutes are always displayed with 2 digits
  const formattedMinutes = minutes.padStart(2, "0");

  return `${formattedHours}:${formattedMinutes} ${period}`;
}

export function groupWorkingHoursByDay(hours: any[]) {
  if (!hours || !Array.isArray(hours)) return [];

  const daysOrder = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];

  // Sort data by day and openTime (operational order, not chronological)
  const sortedData = [...hours].sort((a, b) => {
    const dayDiff =
      daysOrder.indexOf(a.day.toLowerCase()) -
      daysOrder.indexOf(b.day.toLowerCase());
    if (dayDiff !== 0) return dayDiff;

    // Compare openTime within the same day, nulls last
    if (!a.openTime && b.openTime) return 1;
    if (a.openTime && !b.openTime) return -1;
    if (!a.openTime && !b.openTime) return 0;

    // For operational order: if a business operates across midnight (e.g., 10am-5am),
    // we want to show the earlier opening time first (10am before 12am)
    const aTime = a.openTime.split(":").map(Number);
    const bTime = b.openTime.split(":").map(Number);
    const aMinutes = aTime[0] * 60 + aTime[1];
    const bMinutes = bTime[0] * 60 + bTime[1];

    // Check if this day has hours that span midnight
    const dayHours = hours.filter(
      (h) =>
        h.day.toLowerCase() === a.day.toLowerCase() && h.openTime && h.closeTime
    );
    const hasMidnightSpan = dayHours.some((h) => {
      const [openH, openM] = h.openTime.split(":").map(Number);
      const [closeH, closeM] = h.closeTime.split(":").map(Number);
      const openMinutes = openH * 60 + openM;
      const closeMinutes = closeH * 60 + closeM;
      return closeMinutes < openMinutes; // Close time is earlier than open time = spans midnight
    });

    if (hasMidnightSpan) {
      // For midnight-spanning hours, sort by operational order (earliest opening first)
      // This ensures 10am comes before 12am for a 10am-5am business
      return aMinutes - bMinutes;
    } else {
      // For normal hours, use chronological order
      return a.openTime.localeCompare(b.openTime);
    }
  });

  // Group by day
  const groupedData = daysOrder.map((day) => {
    const times = sortedData
      .filter(
        (hour) =>
          hour.day.toLowerCase() === day && hour?.openTime && hour?.closeTime
      )
      .map(({ openTime, closeTime }) => ({ openTime, closeTime }));

    return {
      day,
      times,
    };
  });

  return groupedData;
}

// Interface for closed period data
export interface ClosedPeriod {
  id?: string;
  title?: string;
  start_at?: string;  // REST API format (snake_case)
  end_at?: string;    // REST API format (snake_case)
  startAt?: string;   // GraphQL format (camelCase)
  endAt?: string;     // GraphQL format (camelCase)
}

// Helper function to check if current time falls within any closed period
export function isWithinClosedPeriod(
  closedPeriods: ClosedPeriod[],
  salonTimezone?: string,
  now = new Date(),
): boolean {
  if (
    !isValidTimeZone(salonTimezone) ||
    !closedPeriods ||
    !Array.isArray(closedPeriods) ||
    closedPeriods.length === 0
  ) {
    return false;
  }

  return closedPeriods.some((period) => {
    // Handle both snake_case (REST) and camelCase (GraphQL) formats
    const startAtStr = period.start_at || period.startAt;
    const endAtStr = period.end_at || period.endAt;

    if (!startAtStr || !endAtStr) return false;

    return isRealInstantWithinScheduledWallRange(
      now,
      startAtStr,
      endAtStr,
      salonTimezone,
    );
  });
}

// Helper function to find the end of the current closed period
export function getCurrentClosedPeriodEnd(
  closedPeriods: ClosedPeriod[],
  salonTimezone?: string,
  now = new Date(),
): Date | null {
  if (
    !isValidTimeZone(salonTimezone) ||
    !closedPeriods ||
    !Array.isArray(closedPeriods) ||
    closedPeriods.length === 0
  ) {
    return null;
  }

  const ranges = closedPeriods
    .map((period) => {
      const start = period.start_at || period.startAt;
      const end = period.end_at || period.endAt;
      if (!start || !end) return null;

      try {
        const wallEnd = parseScheduledWall(end);
        if (!wallEnd) return null;
        const { startInstant, endInstant } =
          scheduledWallRangeToRealInstants(start, end, salonTimezone);
        return {
          start: startInstant.getTime(),
          end: endInstant.getTime(),
          wallEnd: wallEnd.getTime(),
        };
      } catch {
        return null;
      }
    })
    .filter(
      (range): range is { start: number; end: number; wallEnd: number } =>
        range !== null && !Number.isNaN(range.wallEnd),
    )
    .sort((a, b) => a.start - b.start);
  const nowEpoch = now.getTime();
  const activeRange = ranges
    .filter((range) => range.start <= nowEpoch && range.end >= nowEpoch)
    .reduce<(typeof ranges)[number] | null>(
      (latest, range) => (!latest || range.end > latest.end ? range : latest),
      null,
    );

  if (!activeRange) return null;

  let closedUntil = activeRange.end;
  let closedUntilWall = activeRange.wallEnd;

  for (const range of ranges) {
    if (range.start <= closedUntil && range.end > closedUntil) {
      closedUntil = range.end;
      closedUntilWall = range.wallEnd;
    }
  }

  return new Date(closedUntilWall);
}

export function roundCurrency(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

export function getEffectiveTotalDurationMinutes(opts: {
  scheduleType?: string | null;
  services: Array<{ duration?: number; extraTime?: number | null; quantity?: number }>;
}): number {
  const scheduleType = String(opts.scheduleType || "sequence").toLowerCase();
  const durations = opts.services.map(
    (s) => ((s.duration || 0) + (s.extraTime || 0)) * (s.quantity || 1),
  );
  if (scheduleType === "parallel") return durations.reduce((m, d) => Math.max(m, d), 0);
  return durations.reduce((sum, d) => sum + d, 0);
}

export function getCurrentOpenStatus(
  hours: any[],
  salonTimezone?: string,
  closedPeriods?: ClosedPeriod[],
  now = new Date(),
) {
  if (!isValidTimeZone(salonTimezone) || !hours || !Array.isArray(hours)) {
    return { isOpen: false, todayHours: null, nextOpenTime: null, closedPeriodEnd: null };
  }

  const currentDate = parseDateOnly(dateOnlyFromInstant(now, salonTimezone))!;
  const wallEpoch = new Date(realInstantAsSalonWallEpoch(now, salonTimezone));
  const currentTime =
    wallEpoch.getUTCHours() * 60 + wallEpoch.getUTCMinutes();

  // First check if we're within a closed period
  const inClosedPeriod = isWithinClosedPeriod(
    closedPeriods || [],
    salonTimezone,
    now,
  );
  if (inClosedPeriod) {
    const closedPeriodEnd = getCurrentClosedPeriodEnd(
      closedPeriods || [],
      salonTimezone,
      now,
    );
    const nextOpenTime = findNextOpenTimeAfterDate(
      hours,
      closedPeriodEnd || wallEpoch,
    );
    return { isOpen: false, todayHours: null, nextOpenTime, closedPeriodEnd };
  }

  // Translate the real current instant to salon wall-clock components.
  const currentDay = weekdayForDateOnly(currentDate).toLowerCase();

  // Get today's hours
  const todayHours = hours.filter(
    (hour) =>
      hour.day.toLowerCase() === currentDay && hour?.openTime && hour?.closeTime
  );
  const previousDate = addDaysToDateOnly(currentDate, -1);
  const previousDay = weekdayForDateOnly(previousDate).toLowerCase();
  const previousCarryHours = hours.filter((hour) => {
    if (
      hour.day.toLowerCase() !== previousDay ||
      !hour?.openTime ||
      !hour?.closeTime
    ) return false;
    const openTime = timeToMinutes(hour.openTime);
    const closeTime = timeToMinutes(hour.closeTime);
    return closeTime < openTime && currentTime < closeTime;
  });

  if (todayHours.length === 0 && previousCarryHours.length === 0) {
    // If no hours today, find next opening time
    const nextOpenTime = findNextOpenTime(
      hours,
      currentDate,
      currentTime,
    );
    return { isOpen: false, todayHours: null, nextOpenTime, closedPeriodEnd: null };
  }

  // Check if currently open in any of today's time slots
  const isOpenToday = todayHours.some((slot) => {
    const openTime = timeToMinutes(slot.openTime);
    const closeTime = timeToMinutes(slot.closeTime);
    return closeTime < openTime
      ? currentTime >= openTime
      : currentTime >= openTime && currentTime < closeTime;
  });
  const isOpen = isOpenToday || previousCarryHours.length > 0;

  // If closed, find next opening time
  const nextOpenTime = isOpen
    ? null
    : findNextOpenTime(hours, currentDate, currentTime);

  return {
    isOpen,
    todayHours: previousCarryHours.length > 0 ? previousCarryHours : todayHours,
    nextOpenTime,
    closedPeriodEnd: null,
  };
}

function timeToMinutes(time: string) {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
}

function activeHoursAt(hours: any[], date: Date, currentTime: number) {
  const day = weekdayForDateOnly(date).toLowerCase();
  const previousDay = weekdayForDateOnly(
    addDaysToDateOnly(date, -1),
  ).toLowerCase();

  return hours.filter((hour) => {
    if (!hour?.openTime || !hour?.closeTime) return false;
    const openTime = timeToMinutes(hour.openTime);
    const closeTime = timeToMinutes(hour.closeTime);

    if (hour.day.toLowerCase() === day) {
      return closeTime < openTime
        ? currentTime >= openTime
        : currentTime >= openTime && currentTime < closeTime;
    }
    return (
      hour.day.toLowerCase() === previousDay &&
      closeTime < openTime &&
      currentTime < closeTime
    );
  });
}

// Helper function to find next open time after a specific date (e.g., after closed period ends)
function findNextOpenTimeAfterDate(
  hours: any[],
  afterDate: Date,
) {
  if (!hours || !Array.isArray(hours)) return null;

  const checkDate = new Date(afterDate);
  const afterTime = checkDate.getUTCHours() * 60 + checkDate.getUTCMinutes();
  const afterDay = weekdayForDateOnly(checkDate).toLowerCase();

  if (activeHoursAt(hours, checkDate, afterTime).length > 0) {
    return {
      day: afterDay,
      time: `${String(checkDate.getUTCHours()).padStart(2, "0")}:${String(
        checkDate.getUTCMinutes(),
      ).padStart(2, "0")}`,
      date: new Date(checkDate),
    };
  }

  // Check same day first (hours after the afterDate time)
  const sameDayHours = hours.filter(
    (hour) =>
      hour.day.toLowerCase() === afterDay && hour?.openTime && hour?.closeTime
  );

  // Sort and find next slot on same day
  const sortedSameDayHours = [...sameDayHours].sort((a, b) => {
    const aTime = a.openTime.split(":").map(Number);
    const bTime = b.openTime.split(":").map(Number);
    return aTime[0] * 60 + aTime[1] - (bTime[0] * 60 + bTime[1]);
  });

  for (const slot of sortedSameDayHours) {
    const [openHours, openMinutes] = slot.openTime.split(":").map(Number);
    const openTime = openHours * 60 + openMinutes;

    if (openTime > afterTime) {
      return { day: afterDay, time: slot.openTime, date: new Date(checkDate) };
    }
  }

  // Check next 90 days (extended to handle longer closed periods)
  for (let i = 1; i <= 90; i++) {
    const nextDate = new Date(checkDate);
    nextDate.setUTCDate(checkDate.getUTCDate() + i);
    const nextDay = weekdayForDateOnly(nextDate).toLowerCase();

    const dayHours = hours.filter(
      (hour) =>
        hour.day.toLowerCase() === nextDay && hour?.openTime && hour?.closeTime
    );

    if (dayHours.length > 0) {
      const sortedHours = [...dayHours].sort((a, b) => {
        const aTime = a.openTime.split(":").map(Number);
        const bTime = b.openTime.split(":").map(Number);
        return aTime[0] * 60 + aTime[1] - (bTime[0] * 60 + bTime[1]);
      });

      return { day: nextDay, time: sortedHours[0].openTime, date: nextDate };
    }
  }

  return null;
}

function findNextOpenTime(
  hours: any[],
  currentDate: Date,
  currentTime: number,
) {
  // Check remaining hours today first
  const currentDay = weekdayForDateOnly(currentDate).toLowerCase();

  const todayHours = hours.filter(
    (hour) =>
      hour.day.toLowerCase() === currentDay && hour?.openTime && hour?.closeTime
  );

  // Sort today's hours by opening time to check them in order
  const sortedTodayHours = [...todayHours].sort((a, b) => {
    const aTime = a.openTime.split(":").map(Number);
    const bTime = b.openTime.split(":").map(Number);
    const aMinutes = aTime[0] * 60 + aTime[1];
    const bMinutes = bTime[0] * 60 + bTime[1];
    return aMinutes - bMinutes;
  });

  // Find next opening time today
  for (const slot of sortedTodayHours) {
    const [openHours, openMinutes] = slot.openTime.split(":").map(Number);
    const openTime = openHours * 60 + openMinutes;

    if (openTime > currentTime) {
      return { day: currentDay, time: slot.openTime, date: new Date(currentDate) };
    }
  }

  // If no more hours today, check next 7 days
  for (let i = 1; i <= 7; i++) {
    const checkDate = new Date(currentDate);
    checkDate.setUTCDate(currentDate.getUTCDate() + i);
    const checkDay = weekdayForDateOnly(checkDate).toLowerCase();

    const dayHours = hours.filter(
      (hour) =>
        hour.day.toLowerCase() === checkDay && hour?.openTime && hour?.closeTime
    );

    if (dayHours.length > 0) {
      // Sort by operational order (for midnight-spanning hours) or chronological order
      const sortedHours = dayHours.sort((a, b) => {
        // Check if this day has hours that span midnight
        const hasMidnightSpan = dayHours.some((h) => {
          const [openH, openM] = h.openTime.split(":").map(Number);
          const [closeH, closeM] = h.closeTime.split(":").map(Number);
          const openMinutes = openH * 60 + openM;
          const closeMinutes = closeH * 60 + closeM;
          return closeMinutes < openMinutes; // Close time is earlier than open time = spans midnight
        });

        if (hasMidnightSpan) {
          // For midnight-spanning hours, sort by operational order (earliest opening first)
          const aTime = a.openTime.split(":").map(Number);
          const bTime = b.openTime.split(":").map(Number);
          const aMinutes = aTime[0] * 60 + aTime[1];
          const bMinutes = bTime[0] * 60 + bTime[1];
          return aMinutes - bMinutes;
        } else {
          // For normal hours, use chronological order
          return a.openTime.localeCompare(b.openTime);
        }
      });
      return { day: checkDay, time: sortedHours[0].openTime, date: checkDate };
    }
  }

  return null;
}

// Promotion utility functions
export function isPromotionActive(promotion?: {
  startAt?: string;
  endAt?: string;
  isActive?: boolean;
}): boolean {
  if (!promotion) return false;

  // If isActive is explicitly set, use it
  if (promotion.isActive !== undefined) {
    if (!promotion.isActive) return false;
  }

  const now = new Date();

  // Check start date
  if (promotion.startAt) {
    const startDate = new Date(promotion.startAt);
    if (now < startDate) return false;
  }

  // Check end date
  if (promotion.endAt) {
    const endDate = new Date(promotion.endAt);
    if (now > endDate) return false;
  }

  return true;
}

export function calculateDiscountedPrice(
  originalPrice: number,
  promotion?: {
    discountType: "percentage" | "fixed";
    discountValue: number;
    startAt?: string;
    endAt?: string;
    isActive?: boolean;
  }
): number {
  if (!promotion || !isPromotionActive(promotion)) {
    return originalPrice;
  }

  if (promotion.discountType === "percentage") {
    const discountAmount = (originalPrice * promotion.discountValue) / 100;
    return Math.max(0, originalPrice - discountAmount);
  } else {
    // Fixed discount
    return Math.max(0, originalPrice - promotion.discountValue);
  }
}

export function getDiscountAmount(
  originalPrice: number,
  promotion?: {
    discountType: "percentage" | "fixed";
    discountValue: number;
    startAt?: string;
    endAt?: string;
    isActive?: boolean;
  }
): number {
  if (!promotion || !isPromotionActive(promotion)) {
    return 0;
  }

  if (promotion.discountType === "percentage") {
    return (originalPrice * promotion.discountValue) / 100;
  } else {
    return promotion.discountValue;
  }
}

export function formatPromotionEndDate(endAt?: string): string {
  if (!endAt) return "";

  try {
    const endDate = new Date(endAt);
    const now = new Date();
    const diffTime = endDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return "";
    if (diffDays === 0) return "Ends today";
    if (diffDays === 1) return "Ends tomorrow";
    if (diffDays <= 7) return `Ends in ${diffDays} days`;

    return `Ends ${endDate.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: endDate.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    })}`;
  } catch {
    return "";
  }
}

/** Max featured/popular services shown on the booking site (matches backend auto-popular limit). */
export const MAX_FEATURED_SERVICES_DISPLAY = 5;

type FeaturedServiceSortable = {
  featuredIndex?: number | null;
};

/** Sort by featured index, then cap count as a client-side fail-safe. */
export function getBookingCartCount({
  selectedServices = [],
  selectedPackages = [],
  giftCardCart,
  selectedProducts = [],
}: {
  selectedServices?: unknown[];
  selectedPackages?: unknown[];
  giftCardCart?: unknown | null;
  selectedProducts?: unknown[];
}): number {
  return (
    selectedServices.length +
    selectedPackages.length +
    (giftCardCart ? 1 : 0) +
    selectedProducts.length
  );
}

export function isProductExpired(expirationDate?: string | null): boolean {
  if (!expirationDate) return false;
  const expiresAt = new Date(expirationDate);
  if (Number.isNaN(expiresAt.getTime())) return false;
  return expiresAt.getTime() < Date.now();
}

export function isProductOutOfStock(stockCount?: number | null): boolean {
  return (stockCount ?? 0) < 1;
}

export function isStorefrontProductOutOfStock(product: {
  inStock?: boolean | null;
  stockCount?: number | null;
}): boolean {
  if (typeof product.inStock === "boolean") return !product.inStock;
  return isProductOutOfStock(product.stockCount);
}

export function isProductListedOnBookingSite(product: {
  listOnBookingSite?: boolean | null;
  isPurchasable?: boolean | null;
}): boolean {
  return (
    product.listOnBookingSite === true && product.isPurchasable !== false
  );
}

/** Fail-closed: storefront APIs/UI require both platform + availability flags. */
export function isProductStorefrontEnabled(business?: {
  productStorefrontEnabled?: boolean | null;
  productStorefrontAvailable?: boolean | null;
} | null): boolean {
  return (
    business?.productStorefrontEnabled === true &&
    business?.productStorefrontAvailable === true
  );
}

/** Load/show products when flags are on and business opted in (or product deep link). */
export function shouldLoadStorefrontProducts(
  business?: {
    productStorefrontEnabled?: boolean | null;
    productStorefrontAvailable?: boolean | null;
    listProductsOnBookingSite?: boolean | null;
  } | null,
  hasProductDeepLink = false,
): boolean {
  if (!isProductStorefrontEnabled(business)) return false;
  return Boolean(business?.listProductsOnBookingSite || hasProductDeepLink);
}

export function sortAndCapPopularServices<
  T extends FeaturedServiceSortable,
>(services: T[], limit = MAX_FEATURED_SERVICES_DISPLAY): T[] {
  return [...services]
    .sort((a, b) => {
      const aIndex = a.featuredIndex ?? null;
      const bIndex = b.featuredIndex ?? null;
      if (aIndex === null && bIndex === null) return 0;
      if (aIndex === null) return 1;
      if (bIndex === null) return -1;
      return aIndex - bIndex;
    })
    .slice(0, limit);
}
